<template>
  <el-col
    :span="24"
    id="checks"
    v-loading.body="(receiving || changingPage) && !navigating"
    element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="checks-header">
      <HeaderInfoText :colspan="9" title="CHECKS" :meta="meta" :selection="selection"></HeaderInfoText>
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage"
          />
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-button plain @click="toggleFilters">
          {{ showFilters ? "Hide filters" : "Show filters" }}
        </el-button>
        <el-button
          type="danger"
          plain
          @click="handleVoidDialogVisible(true)"
          :disabled="!selection.length || !selectionIsVoidable"
          :loading="!!loadingRelated">
          {{ !!loadingRelated ? 'Loading' : 'Void Selected Checks' }}
        </el-button>
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        clearAction="clearCheckState"
        clearMetadataAction="resetCheckMeta"
        searchAction="fetchChecksWithQuery"
        :autoApply="false"
        @doneFiltering="getAppliedFilters"
      >
        <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
          <component :key="i" :is="f.type" :properties="f.props" :ref="`${f.props.filterName}_filter`" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfPaymentList
          v-if="checks"
          name="checks_conf_list"
          ref="checks_conf_list"
          :aggridColumns="aggridColumns"
          :emitSelect="true"
          :rows="checks"
          :currentPage="currentPage"
          :baseQuery="baseQuery"
          @onSelect="handleSelectionChange"
          @handleVoidDialogVisible="handleVoidDialogVisible"
          @postCheck="postCheck"
          @filtersApplied="filtersApplied"
        />
        <el-row v-if="!receiving && !meta.count" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
    <el-dialog
      title="Void Checks"
      :visible="!!voidDialogVisible"
      size="large"
      :show-close="false"
      :close-on-press-escape="false"
      :close-on-click-modal="false">
      <el-row type="flex" align="middle" justify="start" style="padding: 12px 0;">
        <span>
          {{
            `${typeof voidDialogVisible === 'object' && voidDialogVisible.id ? 'This check is' : 'These checks are'}
            related to the following A/Ps. Voiding means you will have to reprint. Are you sure you want to continue?`
          }}
        </span>
      </el-row>
      <el-row type="flex" align="middle" justify="center">
        <el-col :span="24">
          <el-table
            v-if="relatedTransactions.length"
            id="void-ap-table"
            :data="relatedTransactions"
            border
            style="width: 100%; z-index: 0;"
            ref="voidApTable">
            <el-table-column prop="ledger" label="Ledger ID" align="center" width="100"></el-table-column>
            <el-table-column prop="amount_decimal" label="Amount" align="center" width="120">
              <template slot-scope="scope">
                ${{ formatAmount(scope.row.amount_decimal) }}
              </template>
            </el-table-column>
            <el-table-column prop="vendor" label="Vendor" align="left" fit>
              <template slot-scope="scope">
                {{ getVendorById(scope.row.vendor).name }}
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          @click="handleVoidDialogVisible(false)">
          Cancel
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          @click="voidChecks">
          Confirm
        </el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Checks/actionPayload'
  import store from '@/store/store'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { notify } from '@/utils/helpers/Notify'
  import { restoreSelection } from '@/components/RouteComponents/commonFunctions'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'
  import TransactionsConfPaymentList from '@/components/SigmaClientNew/TransactionsConfPaymentList.vue'
  import { retainFiltersOnEntry } from '@/Mixins/SigmaClientNew/transactionGridFilters'

  export default {
    components: {
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText,
      TransactionsConfPaymentList
    },
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        selection: [],
        voidDialogVisible: false,
        relatedTransactions: [],
        changingPage: false,
        postingCheck: null,
        voidingCheck: null,
        loadingRelated: false,
        currentPage: 1,
        aggridColumns: ['rowSelect', 'dateColumn', 'numberColumn', 'amountColumn', 'batchColumn', 'vendorColumn', 'bankColumn', 'actionsColumn']
      }
    },
    methods: {
      getAppliedFilters () {
        const filterModel = this.gridFilterModel()
        let extendedFilters = {}
        if (this.currentFilters.search) extendedFilters.search = this.currentFilters.search
        sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: this.currentPage, extendedFilters: extendedFilters }))
        this.$store.dispatch('setFilter', { page: this.currentPage, ...this.currentFilters })
        this.$refs['checks_conf_list'].applyFiltersonGrid(filterModel)
      },
      gridFilterModel () {
        const asList = (value) => Array.isArray(value) ? value : (value === '' || value === null || value === undefined ? [] : [value])
        const named = (getter, ids) => ids
          .map(id => ({ id, name: (getter(id) || {}).name }))
          .filter(record => record.name)
        const searchModel = (colId, records) => ({
          filterType: colId,
          values: records.map(record => record.id),
          names: records.map(record => record.name)
        })
        const model = {}

        const statuses = asList(this.currentFilters.status__in)
          .reduce((types, value) => types.concat(String(value).split(',')), [])
          .filter(Boolean)
        if (statuses.length) model.status = { filterType: 'set', values: statuses }

        const batches = named(this.getBatchById, asList(this.currentFilters.batch))
        if (batches.length) model.batch = searchModel('batch', batches)

        return model
      },
      async filtersApplied () {
        if (this.currentFilters['status__in']) {
          this.$refs.status_filter[0].selectedVal = this.currentFilters['status__in']
        } else {
          this.$refs.status_filter[0].selectedVal = []
        }

        const batchId = this.currentFilters['batch'] ? this.currentFilters['batch'][0] : ''
        this.syncAutocompleteFilter(this.$refs.batch_filter[0], 'batch', batchId, batchId ? this.getBatchById(batchId) : null)
        if (!batchId) {
          await this.$store.dispatch('unsetFilter', 'batch')
          this.$store.dispatch('clearBatchState')
        }
        this.$store.dispatch('setFilter', { page: this.currentPage, ...this.currentFilters })
      },
      syncAutocompleteFilter (filter, filterName, id, record) {
        if (!filter) return
        const name = (record && record.name) || ''
        filter.keyword = name
        filter.selection = id ? { label: name, value: id } : {}
        if (filter.properties && typeof filter.properties.sync === 'function') {
          filter.properties.sync(filterName, id)
        }
      },
      /**
       * Post a check
       * @param check   The check to be posted
       */
      async postCheck (check) {
        try {
          this.postingCheck = check.id
          const newPayload = Object.assign({}, payload, { data: { status: 'POSTED' } }, { id: check.id })
          const resp = await this.$store.dispatch('update', newPayload)
          if (resp && resp.id) {
            notify(this, 'success', 'Check posted')
          } else {
            throw new Error()
          }
          return resp
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error posting check')
          return e
        } finally {
          this.postingCheck = null
        }
      },
      /**
       * Handles saving a changed status value
       */
      saveChangedStatusValues () {
        this.$store.dispatch('savePc', this.statusValues)
      },
      /**
       * Gets the related transactions for all checks to be voided. Shows/hides the void check modal. If visible is
       * a check object, gets the related transactions for just that check (rather than all checks in this.selection)
       * and sets this.voidDialogVisible to the check object so that it can be accessed after the user chooses to void that
       * single check. Otherwise, voidDialogVisible is simply a boolean.
       * @param visible   Boolean or check object
       */
      async handleVoidDialogVisible (visible) {
        if (!visible) {
          this.voidDialogVisible = false
          return
        }
        const { selection } = this
        let related
        if (typeof visible === 'object' && visible.related_transactions) {
          related = visible.related_transactions
          this.loadingRelated = visible.id
        } else {
          related = [...new Set([].concat(...selection.map(v => v.related_transactions)))]
          this.loadingRelated = true
        }
        try {
          if (Array.isArray(related) && related.length) {
            let promises = [this.$store.dispatch('fetchApsWithQuery', { query: { id__in: related }, storeLines: false })]
            if (related.length > 20) {                // fetch additional pages if aps > 20
              const reqsNeeded = Math.ceil(related.length / 20)
              for (let i = 2; i <= reqsNeeded; i++) {
                promises.push(this.$store.dispatch('fetchApsWithQuery', { query: { id__in: related, page: i }, storeLines: false }))
              }
            }
            const resp = await Promise.all(promises)
            if (resp && resp.length && resp.every(v => v.entities)) {
              this.relatedTransactions = resp.reduce((a, b) => a.concat(Object.values(b.entities.aps)), [])
            }
          }
        } catch (e) {
          console.log(e)
        } finally {
          this.loadingRelated = false
          this.voidDialogVisible = visible
        }
      },
      /**
       * Handles voiding checks, then resets the selection and related transactions. If this.voidDialogVisible is a check
       * object rather than a boolean, sets selection to just that check, and assigns the check id to this.voidingCheck.
       * Otherwise, selection is assigned the value of this.selection, which is an array of selected checks that should
       * be voided.
       */
      async voidChecks () {
        let selection
        if (typeof this.voidDialogVisible === 'object' && this.voidDialogVisible.id) {
          selection = [this.voidDialogVisible]
          this.voidingCheck = this.voidDialogVisible.id
        } else {
          selection = this.selection
        }
        this.handleVoidDialogVisible(false)
        const { relatedTransactions } = this
        if (!(selection.length && relatedTransactions.length)) return
        let promises = []
        selection.forEach(check => {
          if (check.id && check.id > 0) {
            const newPayload = Object.assign({}, payload, { data: { status: 'VOIDED' } }, { id: check.id })
            promises.push(promises.push(this.$store.dispatch('update', newPayload)))
          }
        })
        try {
          const resp = await Promise.all(promises)
          this.selection = []
          this.relatedTransactions = []
          if (resp.length === promises.length) {
            notify(this, 'success', 'Checks and related transactions voided')
          }
        } catch (e) {
          console.log(e)
          notify(this, 'error', 'Error voiding checks and related transactions')
        } finally {
          this.voidingCheck = null
        }
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.selection = [...notThisPage, ...selection]
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        restoreSelection(this, 'checksTable')
      },
      /**
       * Handles paginating between check pages
       * @param page  Target page
       */
      async changePage (page) {
        this.changingPage = true
        const { currentFilters } = this
        let newPayload = currentFilters && Object.keys(currentFilters).length
          ? Object.assign({}, payload, { page, query: currentFilters })
          : Object.assign({}, payload, { page })
        try {
          await this.$store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: false })
          const entities = await this.$store.dispatch('fetchByPage', newPayload)
          let promises = await this.$store.dispatch('findDependencies', entities)
          await Promise.all(promises)
          this.currentPage = page
          let extendedFilters = {}
          if (currentFilters.search) extendedFilters.search = currentFilters.search
          const filterModel = this.gridFilterModel()
          sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: page, extendedFilters: extendedFilters }))
          this.$store.dispatch('setFilter', { page: page, ...this.currentFilters })
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
          this.restoreSelection()
        } catch (e) {
          this.changingPage = false
          console.log(e)
        }
      },
      /**
       * Shows/hides filter pane
       */
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      /**
       * Formats dollar amounts with thousands separators
       * @param value
       * @return {*}
       */
      formatAmount (value) {
        return value ? formatLocale(value) : ''
      }
    },
    computed: {
      ...mapGetters({
        meta: 'getCheckMetadata',
        currentFilters: 'getFilters',
        receiving: 'getCheckFetchStatus',
        getApById: 'getApById',
        getBatchById: 'getBatchById',
        getVendorById: 'getVendorById',
        getCompanyById: 'getCompanyById',
        getBankByBatchId: 'getBankByBatchId',
        navigating: 'getNavigationStatus'
      }),
      selectionIsVoidable () {
        return this.selection.every(v => !['DRAFT', 'VOIDED', 'DELETED'].includes(v.status) && !v.recon)
      },
      checks () {
        return this.$store.getters.getAllCachedChecks
          .filter(v => v.page === this.currentPage)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      },
      baseQuery () {
        return {}
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'status',
              clearable: true,
              opts: [
                { label: 'DRAFT', value: 'DRAFT' },
                { label: 'PRINTED', value: 'POSTED' },
                { label: 'VOIDED', value: 'VOIDED' },
                { label: 'DELETED', value: 'DELETED' }
              ],
              multi: true,
              placeholder: 'Status'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          }
        ]
      }
    },
    async beforeRouteEnter (to, from, next) {
      retainFiltersOnEntry(to.name, from.name)
      try {
        await store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: true })
        next()
      } catch (e) {
        console.log(e)
      }
    },
    mounted () {
      const tableHeight = () => {
        const parentHeight = document.getElementById('checks').offsetHeight
        const headerHeight = document.getElementById('checks-header').offsetHeight
        return parentHeight - headerHeight
      }
      this.tableHeight = tableHeight()
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await Promise.all([
          this.$store.dispatch('clearFilters'),
          this.$store.dispatch('clearApState'),
          this.$store.dispatch('clearCheckState', true)
        ])
        sessionStorage.removeItem(`gridFilterModel:${this.$route.name}`)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  #checks-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Checks/Checks.vue